import { useParams, useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { PageHeader, PageTabs, useSideDrawer } from '@/components/common';
import { useSnackbar } from '@/context/SnackbarContext';
import { api } from '@/utils';
import { CallKeyInfoHeader, CallTranscriptDrawer, CallAudioPlayer, LeadGpt, CallInsights, CallDetails } from '@/components/calls';
import { QUERY_CONFIG, API_ENDPOINTS, buildApiUrl, DRAWER_WIDTH } from '@/constants';

import type { CallData } from '../types/calls';
import type { CombinedTranscript } from '../types/transcript';

const CallPage: React.FC = () => {
    const { id: callId = null } = useParams<{ id: string }>();
    const location = useLocation();
    const backHref = (location.state as { returnUrl?: string })?.returnUrl || '/calls';
    const sideDrawerControls = useSideDrawer();
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const isRefreshingRef = useRef(false);

    const {
        data: call,
        isError,
        error,
        isLoading,
        isRefetching,
    } = useQuery({
        queryKey: ['api', 'calls', callId],
        queryFn: async () => {
            const url = buildApiUrl(API_ENDPOINTS.CALL_BY_ID(Number(callId)));
            const response = await api.get(url, {
                timeout: QUERY_CONFIG.TIMEOUT,
            });

            return response.data as CallData;
        },
        staleTime: 0,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: false,
        enabled: !!callId,
    });

    const qualityScore = call?.qualityScore ?? null;

    // Handle update of call data and call insights when the page is refreshed
    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ['api', 'calls', callId] });
        queryClient.invalidateQueries({ queryKey: ['api', 'call', Number(callId), 'insights'] });
        isRefreshingRef.current = true;
    };

    // Show snackbar when refresh completes
    useEffect(() => {
        if (isRefreshingRef.current && !isRefetching) {
            showSnackbar('Call data updated', 'success');
            isRefreshingRef.current = false;
        }
    }, [isRefetching, showSnackbar]);

    if (isLoading) {
        return (
            <Box mx={3} my={2}>
                <Typography>Loading call details...</Typography>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box mx={3} my={2}>
                <Typography color="error">Error loading call: {(error as Error).message}</Typography>
            </Box>
        );
    }

    if (!call) {
        return (
            <Box mx={3} my={2}>
                <Typography>Call not found</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                width: sideDrawerControls.isOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
                transition: 'width 0.3s ease',
                flexDirection: 'column',
                height: '100vh',
                marginRight: 0,
                position: 'relative',
            }}
        >
            <PageHeader backLink={{ label: 'Back to calls', href: backHref }} onRefresh={handleRefresh} />
            <CallKeyInfoHeader call={call} />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                <PageTabs
                    ariaLabel="Call tabs"
                    tabs={[
                        { label: 'Call insights', id: 'call-insights' },
                        { label: 'LeadGPT', id: 'lead-gpt' },
                    ]}
                >
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
                        <Box sx={{ flex: 1, minWidth: 0, order: { xs: 2, lg: 1 } }}>
                            <CallInsights campaignId={Number(call.campaign_id)} callId={Number(callId)} />
                        </Box>
                        <Box sx={{ width: { xs: '100%', lg: '400px' }, order: { xs: 1, lg: 2 } }}>
                            <CallDetails
                                metadata={call.metadata}
                                transcript={call.parsedTranscript?.transcript as CombinedTranscript[] | null}
                                talkTime={call.talk_time ? Number(call.talk_time) : null}
                                callEndingReason={call.reason_name ? call.reason_name : null}
                                agentSpeechProportion={call.agentSpeechProportion ? call.agentSpeechProportion : undefined}
                                qualityScore={qualityScore}
                            />
                        </Box>
                    </Box>
                    <LeadGpt parsedTranscript={call.parsedTranscript} />
                </PageTabs>
            </Box>
            <CallAudioPlayer callId={callId}>
                <CallTranscriptDrawer parsedTranscript={call.parsedTranscript} sideDrawerControls={sideDrawerControls} />
            </CallAudioPlayer>
        </Box>
    );
};

export default CallPage;
